import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { chatAPI } from '../api';
import { useCompany } from './CompanyContext';
import { useUser } from './UserContext';

const ChatContext = createContext(null);

export function ChatProvider({ children }) {
    const { company } = useCompany();
    const { profile } = useUser();
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(false);
    const historyRef = useRef([]);

    const addMessage = (role, text) => {
        const msg = { id: Date.now() + Math.random(), role, content: text, ts: new Date() };
        setMessages(prev => [...prev, msg]);
        historyRef.current = [...historyRef.current, { role, content: text }];
        return msg;
    };

    const sendMessage = useCallback(async (text) => {
        if (!company || !text.trim()) return;
        addMessage('user', text.trim());
        setLoading(true);
        try {
            const { data } = await chatAPI.send({
                companyId: company.companyId,
                message: text.trim(),
                conversationHistory: historyRef.current.slice(-20),
                callerName: profile.name || null,
                callerPhone: profile.phone || null,
                callerEmail: profile.email || null,
                channel: 'web_chat',
            });
            const reply = data.reply || data.ttsText || data.text || 'Sorry, I had trouble with that.';
            addMessage('assistant', reply);
            return { reply, action: data.action };
        } catch (err) {
            addMessage('assistant', err.response?.data?.error || 'Something went wrong. Please try again.');
        } finally {
            setLoading(false);
        }
    }, [company, profile]);

    const clearChat = useCallback(() => {
        setMessages([]);
        historyRef.current = [];
    }, []);

    const greet = useCallback(() => {
        if (!company) return;
        const persona = company.persona || {};
        const agentName = persona.name || 'GAAF';
        const template = persona.greetingTemplate ||
            `Hi! I'm ${agentName}, your assistant for ${company.companyName}. How can I help you today?`;
        const greeting = template
            .replace('{companyName}', company.companyName)
            .replace('{agentName}', agentName);
        const msg = { id: Date.now(), role: 'assistant', content: greeting, ts: new Date() };
        setMessages([msg]);
        historyRef.current = [{ role: 'assistant', content: greeting }];
    }, [company]);

    return (
        <ChatContext.Provider value={{ messages, loading, sendMessage, clearChat, greet }}>
            {children}
        </ChatContext.Provider>
    );
}

export const useChat = () => useContext(ChatContext);
